import debounce from "lodash/debounce";
import { datadogRum } from "@datadog/browser-rum";
import { createAwsConnectIntent, isAwsConnectIntentReady, isRequestNotDelivered } from "./createAwsConnectIntent";
import { isNullSelector, getElementValue, queryVisibleElement, isElementVisible } from "./domHelpers";
import { AwsConnectComponent, AwsConnectIntent, AwsConnectPage } from "./types";
import { AWS_CONNECT_ARS_ACTION_DISCONNECT, AWS_CONNECT_DISCONNECT_INTENT_TEXT } from "../app-consts/appConstants";

/**
 * Parallel reimplementation of the legacy Jacada/Uniphore bundle's
 * PageController/PageModel/ComponentModel "intent firing" engine
 * (`bundle.js` + `public/scripts/appConfig.js`), scoped to *only* the
 * converse/intents mechanism (page `load`/`timeout` intents and
 * component-click intents) - NOT the RDS website/element event logging,
 * which is out of scope here.
 *
 * Reuses the exact same `window.appConfig.pages/globalIntents/globalEvents`
 * config as the Jacada integration, so both fire on identical triggers, in
 * parallel, independently of one another.
 */

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keypress", "touchstart", "click", "scroll"] as const;
const DOM_SETTLE_DEBOUNCE_MS = 300;
// Guarantees page detection still runs while the DOM never settles (e.g. map
// tiles/markers, ETA updates), so intents like the Rescue Confirmation
// disconnect aren't starved by continuous mutations.
const DOM_SETTLE_MAX_WAIT_MS = 1000;
const MAX_SEND_ATTEMPTS = 3;
// Linear backoff between undelivered load-intent POSTs: 1s, then 2s.
const RETRY_BASE_DELAY_MS = 1000;

// One-time-unless-`replay` guard for "load" type intents, mirrors
// `window.allstateRDS.intentDict`. Scoped to this module only.
let firedIntents: Record<string, boolean> = {};
let retryTimers: ReturnType<typeof setTimeout>[] = [];
let cleanupCurrentPage: () => void = () => {};
let wiredPage: AwsConnectPage | null = null;
let initialized = false;

export const isIntentCondition = (intent: AwsConnectIntent): boolean => {
  if (!intent.conditionalSelector) return false;
  const el = queryVisibleElement(intent.conditionalSelector);
  if (!el) return false;
  const value = getElementValue(el).trim();
  const passesNullSelector = intent.nullSelector?.length ? isNullSelector(intent.nullSelector) : true;
  return passesNullSelector && value === intent.conditionalValue;
};

const shouldFireIntent = (intent: AwsConnectIntent): boolean => {
  return !intent.conditional || isIntentCondition(intent);
};

const isDisconnectIntent = (intent: AwsConnectIntent): boolean => intent.text === AWS_CONNECT_DISCONNECT_INTENT_TEXT;

/**
 * Fires the intent's converse POST, attaching the `disconnect` arsAction
 * when this is the Rescue Tracker's final-submission intent so
 * wle-proxy/drw-proxy tears down the Amazon Connect chat.
 */
const sendIntent = (intent: AwsConnectIntent): Promise<unknown> => {
  const arsAction = isDisconnectIntent(intent) ? AWS_CONNECT_ARS_ACTION_DISCONNECT : undefined;
  return createAwsConnectIntent(intent.text, arsAction);
};

/**
 * Reports a failed converse POST to Datadog. Deliberately omits the request
 * URL/axios error object, which contain the Amazon Connect contactId.
 */
const reportIntentFailure = (intent: AwsConnectIntent, error: unknown, attempt: number, willRetry: boolean): void => {
  const { response, code } = (error || {}) as { response?: { status?: number }; code?: string };
  datadogRum.addError(new Error(`AWS Connect intent "${intent.text}" failed`), {
    awsConnectIntent: intent.text,
    intentType: intent.type,
    isDisconnect: isDisconnectIntent(intent),
    attempt,
    willRetry,
    status: response?.status,
    errorCode: code,
  });
};

const scheduleRetry = (fn: () => void, delayMs: number): void => {
  const timer = setTimeout(() => {
    retryTimers = retryTimers.filter((t) => t !== timer);
    fn();
  }, delayMs);
  retryTimers.push(timer);
};

const clearRetryTimers = (): void => {
  retryTimers.forEach(clearTimeout);
  retryTimers = [];
};

/**
 * POSTs a load intent, keeping it marked as fired while in flight. Every
 * failure is reported. Only failures where the request provably never reached
 * wle-proxy (see `isRequestNotDelivered`) are retried, with backoff, up to
 * MAX_SEND_ATTEMPTS - anything else may already have been acted on, so
 * resending could duplicate it. Once given up on, the intent stays marked so
 * page re-evaluations don't resend it.
 */
const attemptLoadIntent = (intent: AwsConnectIntent, page: AwsConnectPage | null, attempt: number): void => {
  firedIntents[intent.text] = true;
  sendIntent(intent).catch((error) => {
    const willRetry = isRequestNotDelivered(error) && attempt < MAX_SEND_ATTEMPTS;
    reportIntentFailure(intent, error, attempt, willRetry);
    if (willRetry) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      scheduleRetry(() => retryLoadIntent(intent, page, attempt + 1), RETRY_BASE_DELAY_MS * attempt);
    }
  });
};

/**
 * The disconnect is always retried so the Amazon Connect chat is torn down
 * even if the user has navigated on. Any other intent is only retried while
 * its page is still current and its condition still holds; otherwise it's
 * un-marked so it can fire afresh if the user returns to that page.
 */
const retryLoadIntent = (intent: AwsConnectIntent, page: AwsConnectPage | null, attempt: number): void => {
  const stillRelevant = isDisconnectIntent(intent) || (wiredPage === page && shouldFireIntent(intent));
  if (!stillRelevant || !isAwsConnectIntentReady()) {
    delete firedIntents[intent.text];
    return;
  }
  attemptLoadIntent(intent, page, attempt);
};

/**
 * Only marks a load intent as fired once it is actually sent. If its
 * condition isn't met yet, or the contactId/appId/partnerCode context isn't
 * available yet, it stays pending and is retried on the next page evaluation.
 */
const fireLoadIntent = (intent: AwsConnectIntent): void => {
  if (!intent.replay && firedIntents[intent.text]) return;
  if (!shouldFireIntent(intent) || !isAwsConnectIntentReady()) return;
  attemptLoadIntent(intent, wiredPage, 1);
};

/**
 * Arms inactivity timers for a set of "timeout" intents, restarting them only
 * on user-activity events (mirrors `lib.timeout`) - never on DOM changes.
 * Conditions are checked when the timer elapses, so a condition that becomes
 * true while the user is idle on the page is still honoured.
 *
 * Timeout intents are always treated as `replay: true`: any `replay` value in
 * the shared appConfig is ignored for them, so they fire again after every
 * new inactivity period (and again on each page visit). `replay` only gates
 * "load" intents. Each call owns
 * its own timers so tearing down one set never cancels another. Returns a
 * teardown function.
 */
const armTimeoutIntents = (timeoutIntents: AwsConnectIntent[]): (() => void) => {
  if (!timeoutIntents.length) return () => {};

  let timers: ReturnType<typeof setTimeout>[] = [];
  const clearTimers = (): void => {
    timers.forEach(clearTimeout);
    timers = [];
  };

  const reset = (): void => {
    clearTimers();
    timeoutIntents.forEach((intent) => {
      if (intent.inactivity === undefined) return;
      timers.push(
        setTimeout(() => {
          if (shouldFireIntent(intent)) {
            sendIntent(intent).catch((error) => reportIntentFailure(intent, error, 1, false));
          }
        }, intent.inactivity)
      );
    });
  };

  ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, reset));
  reset();

  return () => {
    ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, reset));
    clearTimers();
  };
};

const getLoadIntents = (intents: AwsConnectIntent[] = []): AwsConnectIntent[] =>
  intents.filter((intent) => intent.type === "load");

const wirePageIntents = (
  pageIntents: AwsConnectIntent[] = [],
  globalIntents: AwsConnectIntent[] = []
): (() => void) => {
  const timeoutIntents: AwsConnectIntent[] = [];

  [...pageIntents, ...globalIntents].forEach((intent) => {
    if (intent.type === "load") {
      fireLoadIntent(intent);
    } else if (intent.type === "timeout") {
      timeoutIntents.push(intent);
    }
  });

  return armTimeoutIntents(timeoutIntents);
};

const fireComponentIntents = (intents: AwsConnectIntent[] = []): (() => void) => {
  const cleanupFns: (() => void)[] = [];
  intents.forEach((intent) => {
    if (intent.type === "load") {
      fireLoadIntent(intent);
    } else if (intent.type === "timeout" && shouldFireIntent(intent)) {
      cleanupFns.push(armTimeoutIntents([intent]));
    }
  });
  return () => cleanupFns.forEach((fn) => fn());
};

/**
 * Wires click-delegated listeners for each component's `intents`, matching
 * the legacy ComponentModel's click-triggered intent firing. Delegated on
 * `document.body` so it works regardless of when React mounts the element.
 */
const wireComponentIntents = (
  components: AwsConnectComponent[] = [],
  globalEvents: AwsConnectComponent[] = []
): (() => void) => {
  const cleanupFns: (() => void)[] = [];

  [...components, ...globalEvents].forEach((component) => {
    if (!component.intents?.length || !component.selector) return;

    let componentCleanup: (() => void) = () => {};
    const handler = (event: Event): void => {
      const target = event.target as Element | null;
      if (!target?.closest?.(component.selector)) return;
      componentCleanup();
      componentCleanup = fireComponentIntents(component.intents);
    };

    document.body.addEventListener("click", handler);
    cleanupFns.push(() => {
      document.body.removeEventListener("click", handler);
      componentCleanup();
    });
  });

  return () => cleanupFns.forEach((fn) => fn());
};

const isPage = (page: AwsConnectPage): boolean => {
  const titleEl = document.querySelector(page.titleSelector);
  if (!titleEl) return false;

  const visible = isElementVisible(titleEl);
  const text = (titleEl.textContent || "").trim();
  const passesNullSelector = isNullSelector(page.nullSelector);

  if (page.titleValue?.length) {
    return visible && passesNullSelector && page.titleValue.includes(text);
  }
  return visible && passesNullSelector;
};

const teardownCurrentPage = (): void => {
  cleanupCurrentPage();
  cleanupCurrentPage = () => {};
  wiredPage = null;
};

/**
 * Finds the page (from `window.appConfig.pages`) matching the current DOM.
 * If it's the page already wired, only pending load intents are retried -
 * listeners and inactivity timers are left untouched so DOM churn (ETA
 * updates, map tiles, spinners) doesn't restart them. On a page change, tears
 * down the previous page and wires up the new one.
 */
export const evaluateCurrentPage = (): void => {
  const appConfig = window.appConfig;
  const currentPage = appConfig?.pages ? Object.values(appConfig.pages).find(isPage) : undefined;

  if (!appConfig || !currentPage) {
    teardownCurrentPage();
    return;
  }

  if (currentPage === wiredPage) {
    [...getLoadIntents(currentPage.intents), ...getLoadIntents(appConfig.globalIntents)].forEach(fireLoadIntent);
    return;
  }

  teardownCurrentPage();
  wiredPage = currentPage;
  const cleanupIntents = wirePageIntents(currentPage.intents, appConfig.globalIntents);
  const cleanupComponents = wireComponentIntents(currentPage.components, appConfig.globalEvents);
  cleanupCurrentPage = () => {
    cleanupIntents();
    cleanupComponents();
  };
};

const debouncedEvaluate = debounce(evaluateCurrentPage, DOM_SETTLE_DEBOUNCE_MS, {
  maxWait: DOM_SETTLE_MAX_WAIT_MS,
});

/**
 * Boots the engine: patches history.pushState/replaceState (independent of
 * whether the Jacada bundle also does so - patches safely chain) and
 * observes the DOM for changes, re-evaluating the current page/intents on
 * every route change or significant DOM update. Idempotent - safe to call
 * more than once.
 */
export const initAwsConnectIntentEngine = (): (() => void) => {
  if (initialized) return () => {};
  initialized = true;
  firedIntents = {};

  const pushState = history.pushState;
  const replaceState = history.replaceState;

  history.pushState = function (...args: Parameters<History["pushState"]>) {
    const result = pushState.apply(this, args);
    debouncedEvaluate();
    return result;
  };
  history.replaceState = function (...args: Parameters<History["replaceState"]>) {
    const result = replaceState.apply(this, args);
    debouncedEvaluate();
    return result;
  };

  window.addEventListener("popstate", debouncedEvaluate);
  window.addEventListener("locationchange", debouncedEvaluate);

  const observer = new MutationObserver(debouncedEvaluate);
  observer.observe(document.body, { attributes: true, childList: true, subtree: true, characterData: true });

  debouncedEvaluate();

  return () => {
    initialized = false;
    history.pushState = pushState;
    history.replaceState = replaceState;
    window.removeEventListener("popstate", debouncedEvaluate);
    window.removeEventListener("locationchange", debouncedEvaluate);
    observer.disconnect();
    debouncedEvaluate.cancel();
    clearRetryTimers();
    teardownCurrentPage();
  };
};
